import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Mail,
  Play,
  Pause,
  RotateCw,
  ChevronDown,
  ChevronUp,
  Send,
  MousePointerClick,
  CheckCircle2,
  Clock,
  AlertCircle,
  Plus,
  Loader2,
  Users,
  X,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import {
  listCampaigns,
  startCampaignSend,
  getCampaignSendStatus,
  pauseCampaignSend,
  previewFreelancerUpdate,
  createCampaign,
  getDefaultTemplate,
  type CampaignSummary,
  type CampaignSendStatus,
  type FreelancerSearchResult,
} from "@/services/campaigns-api";
import { SugestoesDestinatarios } from "@/components/campanhas/SugestoesDestinatarios";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toUserError } from "@/lib/api-error";

// Enviar e-mail é efeito externo e irreversível: sempre confirmar com o número.
function ConfirmarEnvioDialog({
  open,
  onOpenChange,
  quantidade,
  retomando,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quantidade: number;
  retomando: boolean;
  onConfirm: () => void;
}) {
  const emails = `${quantidade.toLocaleString("pt-BR")} ${quantidade === 1 ? "e-mail" : "e-mails"}`;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent onClick={(event) => event.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>{retomando ? "Retomar o envio?" : "Iniciar o envio?"}</AlertDialogTitle>
          <AlertDialogDescription>
            Vai enviar até {emails} agora. E-mail enviado não pode ser desfeito; dá para pausar no meio.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Agora não</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Enviar {emails}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function useCampaignStatus(campaignId: string) {
  return useQuery<CampaignSendStatus>({
    queryKey: ["campaign-status", campaignId],
    queryFn: () => getCampaignSendStatus(campaignId),
    refetchInterval: (query) => (query.state.data?.job?.status === "running" ? 3000 : false),
  });
}

// Template padrao para emails de campanha
const DEFAULT_EMAIL_TEMPLATE = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Atualize seu Cadastro - Corisco</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #2d3748; background-color: #f7fafc; }
        .email-container { max-width: 600px; margin: 20px auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08); }
        .header { background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%); color: white; padding: 40px 30px; text-align: center; }
        .logo img { max-height: 70px; filter: brightness(0) invert(1); }
        .content { padding: 50px 40px; text-align: justify; }
        .greeting { font-size: 20px; margin-bottom: 20px; font-weight: 400; }
        .message { font-size: 16px; line-height: 1.6; color: #4a5568; margin-bottom: 25px; }
        .section-title { font-size: 16px; font-weight: 600; margin: 30px 0 15px 0; color: #2d3748; }
        .benefit-list { margin: 20px 0; }
        .benefit-item { font-size: 15px; color: #4a5568; margin-bottom: 12px; padding-left: 20px; position: relative; }
        .benefit-item:before { content: "\\01F539"; position: absolute; left: 0; }
        .cta-container { text-align: center; margin: 40px 0; }
        .cta-button { display: inline-block; background: #4299e1; color: white; text-decoration: none; padding: 15px 35px; border-radius: 6px; font-size: 16px; font-weight: 500; }
        .cta-button:hover { background: #3182ce; }
        .tip-section { background: #f8f9fa; padding: 20px; border-radius: 6px; margin: 30px 0; border-left: 3px solid #4299e1; }
        .tip-title { font-size: 16px; font-weight: 600; margin-bottom: 8px; color: #2d3748; }
        .unsubscribe-box { background: #fafafa; padding: 20px; border-radius: 6px; text-align: center; margin: 30px 0; }
        .unsubscribe-title { font-size: 14px; color: #718096; font-weight: 600; margin-bottom: 8px; }
        .unsubscribe-text { font-size: 13px; color: #a0aec0; }
        .unsubscribe-text a { color: #4299e1; text-decoration: none; }
        .footer { background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%); color: #e2e8f0; text-align: center; padding: 32px 24px; font-size: 14px; }
        .footer a { color: #90cdf4; text-decoration: none; }
        .social-links { margin-top: 15px; }
        .social-links a { color: white; text-decoration: none; margin: 0 10px; }
    </style>
</head>
<body>
    <div class="email-container">
        <div class="header">
            <div class="logo">
                <img src="https://www.coriscofrila.com.br/assets/logo.43e5e750.png" alt="Corisco" />
            </div>
        </div>
        <div class="content">
            <div class="greeting">Olá, {nome}! 👋</div>
            <div class="message">Esperamos que você esteja bem! 😊</div>
            <div class="message">Estamos muito animados com a evolução da nossa plataforma e gostaríamos de contar com apenas <strong>5 minutinhos</strong> do seu tempo para atualizar suas informações conosco.</div>
            <div class="section-title">Por que atualizar seu cadastro?</div>
            <div class="benefit-list">
                <div class="benefit-item"><strong><span style="color: #4299e1; font-weight: bold; margin-right: 8px;">•</span>Acompanhar sua evolução profissional:</strong> Sabemos que seus interesses e habilidades podem ter mudado e queremos conhecê-los melhor!</div>
                <div class="benefit-item"><strong><span style="color: #4299e1; font-weight: bold; margin-right: 8px;">•</span>Alinhar vagas à sua expectativa salarial:</strong> Sua carreira evolui, e sua remuneração também. Queremos estar em sintonia com esse momento.</div>
                <div class="benefit-item"><strong><span style="color: #4299e1; font-weight: bold; margin-right: 8px;">•</span>Aumentar suas chances de novas oportunidades:</strong> Cadastros atualizados ganham mais visibilidade e combinam melhor com os perfis buscados.</div>
                <div class="benefit-item"><strong><span style="color: #4299e1; font-weight: bold; margin-right: 8px;">•</span>Garantir mais segurança nas nossas comunicações.</strong></div>
            </div>
            <div class="section-title">Atualizar é rápido e fácil!</div>
            <div class="message">Clique no botão abaixo:</div>
            <div class="cta-container">
                <a href="{form_url}" class="cta-button">👉 ATUALIZAR MEU CADASTRO</a>
            </div>
            <div class="message"><strong>Leva só 5 minutos!</strong> Seus dados antigos já vêm preenchidos, basta revisar e ajustar o que for necessário.</div>
            <div class="tip-section">
                <div class="tip-title">Dica especial ❤️:</div>
                <div>Seja específico nas respostas! Quanto mais detalhes você compartilhar, melhor conseguiremos entender seu perfil e objetivos.</div>
            </div>
            <div class="unsubscribe-box">
                <div class="unsubscribe-title">Não quer mais fazer parte do nosso banco de talentos?</div>
                <div class="unsubscribe-text">Sentiremos muito a sua falta 😔, mas, se for o caso, você pode se descadastrar clicando <a href="{unsubscribe_url}">aqui</a>.</div>
            </div>
            <div class="message">Se tiver dúvidas ou precisar de ajuda, nossa equipe está sempre disponível para te apoiar.</div>
            <div class="message"><strong>Muito obrigado por fazer parte da Corisco Frilas! 🌟</strong></div>
            <div class="message"><strong>Atenciosamente,</strong><br>Equipe Corisco Frilas</div>
        </div>
        <div class="footer">
            <p>&copy; 2024 Corisco. Todos os direitos reservados.</p>
            <div class="social-links">
                <a href="#">📧 adm@coriscofrila.com.br</a> |
                <a href="#">🌐 www.coriscofrila.com.br</a>
            </div>
        </div>
    </div>
</body>
</html>`;

const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  freelancer_update: "Atualiz. de cadastro",
  manual: "Seleção manual",
};

function getCampaignTypeLabel(type: string): string {
  return CAMPAIGN_TYPE_LABELS[type] || type;
}

function getCampaignStatus(campaign: CampaignSummary): {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
} {
  if (campaign.pending === 0 && campaign.total_records > 0) {
    return { label: "Concluída", variant: "outline" };
  }
  if (campaign.pending === campaign.total_records) {
    return { label: "Pendente", variant: "secondary" };
  }
  return { label: "Em andamento", variant: "default" };
}

// ============================================
// Autocomplete de freelancers
// ============================================

function FreelancerAutocomplete({
  selected,
  onAdd,
  onRemove,
}: {
  selected: FreelancerSearchResult[];
  onAdd: (f: FreelancerSearchResult) => void;
  onRemove: (email: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // PERF: busca server-side (≤25 ativos) em vez de carregar a tabela inteira
  // (~5,5k) via cache só para um autocomplete.
  const { data: searchResults = [] } = useQuery({
    queryKey: ["campanha-freelancer-search", query.trim()],
    enabled: query.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async () => {
      // Neutraliza wildcards do ilike (% _) e a sintaxe do PostgREST .or() ( , ( ) * \ )
      // para evitar injeção de filtro a partir do texto digitado.
      const q = query.trim().replace(/[%_,()*\\]/g, " ");
      const { data, error } = await supabase
        .from("freelancers")
        .select("nome, email, is_active, unsubscribed_at")
        .or(`nome.ilike.%${q}%,email.ilike.%${q}%`)
        .is("unsubscribed_at", null)
        .limit(25);
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedEmails = new Set(selected.map((s) => s.email));

  const filteredResults = (searchResults as Array<{ email: string | null; nome: string | null; is_active: boolean | null }>)
    .filter((f) => !!f.email && !selectedEmails.has(f.email) && f.is_active !== false)
    .slice(0, 20)
    .map((f) => ({ email: f.email as string, nome: f.nome || "" }));

  return (
    <div className="space-y-3">
      <div ref={containerRef} className="relative">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou email..."
            className="pl-9"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
          />
        </div>
        {showResults && query.length >= 2 && (
          <div className="absolute z-50 w-full mt-1 rounded-md border bg-popover shadow-md max-h-48 overflow-auto">
            {filteredResults.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground text-center">
                Nenhum resultado
              </div>
            ) : (
              filteredResults.map((f) => (
                <button
                  key={f.email}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                  onClick={() => {
                    onAdd(f);
                    setQuery("");
                    setShowResults(false);
                  }}
                >
                  <span className="font-medium">{f.nome}</span>
                  <span className="text-muted-foreground ml-2">{f.email}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((f) => (
            <Badge key={f.email} variant="secondary" className="gap-1 pr-1">
              {f.nome ? `${f.nome} (${f.email})` : f.email}
              <button
                type="button"
                onClick={() => onRemove(f.email)}
                className="ml-1 hover:bg-muted rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {selected.length} freelancer{selected.length !== 1 ? "s" : ""} selecionado{selected.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}

// ============================================
// Modal de criacao de campanha
// ============================================

function CreateCampaignDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [campaignType, setCampaignType] = useState("freelancer_update");
  const [emailContent, setEmailContent] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [selectedFreelancers, setSelectedFreelancers] = useState<FreelancerSearchResult[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: preview, isLoading: isLoadingPreview } = useQuery({
    queryKey: ["freelancer-update-preview"],
    queryFn: previewFreelancerUpdate,
    enabled: open && campaignType === "freelancer_update",
  });

  // Carrega template padrao ao abrir o dialog
  const { data: templateData } = useQuery({
    queryKey: ["default-template", campaignType],
    queryFn: () => getDefaultTemplate(campaignType),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      // Usa template da ultima campanha, ou o hardcoded se nao tiver
      const template = templateData?.email_content || DEFAULT_EMAIL_TEMPLATE;
      setEmailContent(template);
    }
  }, [open, templateData]);

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setName("");
      setEmailContent("");
      setCampaignType("freelancer_update");
      setSelectedFreelancers([]);
    }
  }, [open]);

  const previewHtml = emailContent
    .replace(/\{nome\}/g, "João Silva")
    .replace(/\{form_url\}/g, "#")
    .replace(/\{unsubscribe_url\}/g, "#");

  const canCreate =
    name.trim() &&
    emailContent.trim() &&
    (campaignType === "freelancer_update" || selectedFreelancers.length > 0);

  const handleCreate = async () => {
    if (!canCreate) return;
    setIsCreating(true);
    try {
      const result = await createCampaign({
        name: name.trim(),
        campaign_type: campaignType,
        email_content: emailContent.trim(),
        emails: campaignType === "manual" ? selectedFreelancers.map((f) => f.email) : undefined,
      });
      toast({
        title: "Campanha criada",
        description: `${result.records_added} freelancers adicionados`,
      });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      onOpenChange(false);
    } catch (error) {
      const msg = toUserError(error).description;
      toast({ title: "Erro ao criar campanha", description: msg, variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Campanha</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Tipo de campanha</label>
            <Select value={campaignType} onValueChange={setCampaignType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="freelancer_update">
                  Freelancers sem atualizar perfil (3+ meses)
                </SelectItem>
                <SelectItem value="manual">
                  Seleção manual de freelancers
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {campaignType === "freelancer_update" && (
            <div className="flex items-center gap-2 rounded-md bg-muted p-3 text-sm">
              <Users className="h-4 w-4 text-muted-foreground shrink-0" />
              {isLoadingPreview ? (
                <span className="text-muted-foreground">Calculando...</span>
              ) : (
                <span>
                  <strong>{preview?.eligible ?? 0}</strong> freelancers elegíveis de{" "}
                  <strong>{preview?.total ?? 0}</strong> total
                </span>
              )}
            </div>
          )}

          {campaignType === "manual" && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Selecionar freelancers</label>
              <FreelancerAutocomplete
                selected={selectedFreelancers}
                onAdd={(f) => setSelectedFreelancers((prev) => [...prev, f])}
                onRemove={(email) =>
                  setSelectedFreelancers((prev) => prev.filter((f) => f.email !== email))
                }
              />
              <SugestoesDestinatarios
                selected={selectedFreelancers}
                onAdd={(f) =>
                  setSelectedFreelancers((prev) =>
                    prev.some((selecionado) => selecionado.email === f.email) ? prev : [...prev, f],
                  )
                }
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Nome da campanha</label>
            <Input
              placeholder="Ex: Campanha Atualização - Fevereiro 2026"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Conteúdo do email (HTML)</label>
            <Tabs defaultValue="preview">
              <TabsList className="mb-2">
                <TabsTrigger value="preview">Preview</TabsTrigger>
                <TabsTrigger value="editor">Editor</TabsTrigger>
              </TabsList>
              <TabsContent value="preview">
                <div className="border rounded-md overflow-hidden bg-white">
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full min-h-[400px] border-0"
                    title="Preview do email"
                    sandbox="allow-same-origin"
                  />
                </div>
              </TabsContent>
              <TabsContent value="editor">
                <Textarea
                  placeholder="HTML do email. Use {nome}, {form_url} e {unsubscribe_url} como variáveis."
                  className="min-h-[200px] font-mono text-xs"
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Variáveis: {"{nome}"}, {"{form_url}"}, {"{unsubscribe_url}"}
                </p>
              </TabsContent>
            </Tabs>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={isCreating || !canCreate}>
            {isCreating && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Criar Campanha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Painel de detalhes da campanha
// ============================================

function CampaignDetail({
  campaign,
  onClose,
}: {
  campaign: CampaignSummary;
  onClose: () => void;
}) {
  const [limit, setLimit] = useState<string>("");
  const [isStarting, setIsStarting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: status } = useCampaignStatus(campaign.id);

  const jobStatus = status?.job?.status;
  const counts = status?.db_counts;
  // `??` e não `||`: zero ao vivo é zero, não o número velho da listagem.
  const totalRecords = counts?.total ?? campaign.total_records;
  const sentCount = counts?.sent ?? campaign.sent;
  const pendingCount = counts?.pending ?? campaign.pending;
  const clickedCount = counts?.clicked ?? campaign.clicked;
  const doneCount = counts?.done ?? campaign.done;
  const failedCount = status?.job?.failed || 0;

  const progressPercent =
    totalRecords > 0 ? ((totalRecords - pendingCount) / totalRecords) * 100 : 0;

  const handleStart = async () => {
    setIsStarting(true);
    try {
      const parsedLimit = limit ? parseInt(limit, 10) : undefined;
      await startCampaignSend(campaign.id, parsedLimit);
      toast({ title: "Envio iniciado" });
      queryClient.invalidateQueries({ queryKey: ["campaign-status", campaign.id] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    } catch (error) {
      const msg = toUserError(error).description;
      toast({ title: "Erro ao iniciar envio", description: msg, variant: "destructive" });
    } finally {
      setIsStarting(false);
    }
  };

  const handlePause = async () => {
    setIsPausing(true);
    try {
      await pauseCampaignSend(campaign.id);
      toast({ title: "Pausa solicitada" });
      queryClient.invalidateQueries({ queryKey: ["campaign-status", campaign.id] });
    } catch (error) {
      const msg = toUserError(error).description;
      toast({ title: "Erro ao pausar", description: msg, variant: "destructive" });
    } finally {
      setIsPausing(false);
    }
  };

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{campaign.name}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Barra de progresso */}
        <div>
          <div className="flex justify-between text-sm text-muted-foreground mb-1">
            <span>Progresso</span>
            <span>
              {totalRecords - pendingCount} / {totalRecords}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Contadores */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>Pendentes: <strong>{pendingCount}</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Send className="h-4 w-4 text-blue-500" />
            <span>Enviados: <strong>{sentCount}</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <MousePointerClick className="h-4 w-4 text-green-500" />
            <span>Clicados: <strong>{clickedCount}</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <span>Concluídos: <strong>{doneCount}</strong></span>
          </div>
          {failedCount > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span>Falhas: <strong>{failedCount}</strong></span>
            </div>
          )}
        </div>

        {/* Status do job */}
        {status?.job && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Status do job:</span>
            <Badge
              variant={
                status.job.status === "running"
                  ? "default"
                  : status.job.status === "failed"
                    ? "destructive"
                    : "secondary"
              }
            >
              {status.job.status === "running"
                ? "Enviando"
                : status.job.status === "paused"
                  ? "Pausado"
                  : status.job.status === "completed"
                    ? "Concluído"
                    : status.job.status === "failed"
                      ? "Erro"
                      : status.job.status}
            </Badge>
            {status.job.error && (
              <span className="text-destructive text-xs">{status.job.error}</span>
            )}
          </div>
        )}

        {/* Controles */}
        {pendingCount > 0 && (
          <div className="flex items-center gap-3 pt-2">
            {jobStatus === "running" ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={handlePause}
                disabled={isPausing}
              >
                <Pause className="h-4 w-4 mr-1" />
                {isPausing ? "Pausando..." : "Pausar"}
              </Button>
            ) : (
              <>
                <Input
                  type="number"
                  placeholder="Limite (opcional)"
                  className="w-40 h-9"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                />
                <Button size="sm" onClick={() => setConfirmando(true)} disabled={isStarting}>
                  {jobStatus === "paused" ? (
                    <RotateCw className="h-4 w-4 mr-1" />
                  ) : (
                    <Play className="h-4 w-4 mr-1" />
                  )}
                  {isStarting
                    ? "Iniciando..."
                    : jobStatus === "paused"
                      ? "Retomar"
                      : "Iniciar Envio"}
                </Button>
              </>
            )}
          </div>
        )}
        <ConfirmarEnvioDialog
          open={confirmando}
          onOpenChange={setConfirmando}
          quantidade={Math.min(pendingCount, parseInt(limit, 10) || pendingCount)}
          retomando={jobStatus === "paused"}
          onConfirm={() => void handleStart()}
        />
      </CardContent>
    </Card>
  );
}

// ============================================
// Botoes de acao inline na tabela
// ============================================

function CampaignActions({ campaign }: { campaign: CampaignSummary }) {
  const [isStarting, setIsStarting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  // Status real do job (mesmo cache do detalhe): sem isto a linha mostrava
  // "Iniciar" com envio rodando e deixava iniciar duas vezes.
  const { data: status } = useCampaignStatus(campaign.id);
  const jobStatus = status?.job?.status;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const campaignStatus = getCampaignStatus(campaign);
  const isCompleted = campaignStatus.label === "Concluída";

  if (isCompleted) return null;

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await startCampaignSend(campaign.id);
      toast({ title: "Envio iniciado" });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-status", campaign.id] });
    } catch (error) {
      const msg = toUserError(error).description;
      toast({ title: "Erro ao iniciar", description: msg, variant: "destructive" });
    } finally {
      setIsStarting(false);
    }
  };

  const handlePause = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPausing(true);
    try {
      await pauseCampaignSend(campaign.id);
      toast({ title: "Pausa solicitada" });
      queryClient.invalidateQueries({ queryKey: ["campaign-status", campaign.id] });
    } catch (error) {
      const msg = toUserError(error).description;
      toast({ title: "Erro ao pausar", description: msg, variant: "destructive" });
    } finally {
      setIsPausing(false);
    }
  };

  if (jobStatus === "running") {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={handlePause}
        disabled={isPausing}
        className="h-8 px-2"
      >
        <Pause className="h-3.5 w-3.5 mr-1" />
        Pausar
      </Button>
    );
  }

  const pendentes = status?.db_counts?.pending ?? campaign.pending;
  if (pendentes === 0) return null;

  return (
    <>
    <ConfirmarEnvioDialog
      open={confirmando}
      onOpenChange={setConfirmando}
      quantidade={pendentes}
      retomando={jobStatus === "paused"}
      onConfirm={() => void handleStart()}
    />
    <Button
      variant="ghost"
      size="sm"
      onClick={(event) => {
        event.stopPropagation();
        setConfirmando(true);
      }}
      disabled={isStarting}
      className="h-8 px-2"
    >
      {jobStatus === "paused" ? (
        <>
          <RotateCw className="h-3.5 w-3.5 mr-1" />
          Retomar
        </>
      ) : (
        <>
          <Play className="h-3.5 w-3.5 mr-1" />
          Iniciar
        </>
      )}
    </Button>
    </>
  );
}

// ============================================
// Pagina principal
// ============================================

export default function Campanhas() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data: campaigns = [], isLoading, isError, refetch } = useQuery<CampaignSummary[]>({
    queryKey: ["campaigns"],
    queryFn: listCampaigns,
  });

  // Totais para os cards de resumo
  const totalCampaigns = campaigns.length;
  const totalSent = campaigns.reduce((acc, c) => acc + c.sent, 0);
  const totalClicked = campaigns.reduce((acc, c) => acc + c.clicked, 0);
  const taxaCliques = totalSent > 0 ? (totalClicked / totalSent) * 100 : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campanhas de Email"
        actions={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Nova Campanha
          </Button>
        }
      />

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Campanhas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{totalCampaigns}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Emails Enviados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold">{totalSent}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Taxa de Cliques
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <MousePointerClick className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold">{taxaCliques.toFixed(1)}%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de campanhas */}
      {isLoading ? (
        <LoadingState variant="list" />
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : campaigns.length === 0 ? (
        <EmptyState
          icon={Mail}
          title="Nenhuma campanha encontrada"
          description="Crie uma campanha para começar a enviar emails aos freelancers."
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead className="text-center">Pendentes</TableHead>
                  <TableHead className="text-center">Enviados</TableHead>
                  <TableHead className="text-center">Clicados</TableHead>
                  <TableHead className="text-center">Concluídos</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => {
                  const statusInfo = getCampaignStatus(campaign);
                  return (
                    <TableRow
                      key={campaign.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() =>
                        setExpandedId(
                          expandedId === campaign.id ? null : campaign.id
                        )
                      }
                    >
                      <TableCell className="font-medium">{campaign.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getCampaignTypeLabel(campaign.campaign_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {campaign.total_records}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{campaign.pending}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-blue-500/10 text-blue-500 hover:bg-blue-500/20">
                          {campaign.sent}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">
                          {campaign.clicked}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20">
                          {campaign.done}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {campaign.created_at
                          ? format(
                              new Date(campaign.created_at),
                              "dd/MM/yyyy",
                              { locale: ptBR }
                            )
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <CampaignActions campaign={campaign} />
                          {expandedId === campaign.id ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
        </div>
      )}

      {/* Painel de detalhes expandido */}
      {expandedId && campaigns.find((c) => c.id === expandedId) && (
        <CampaignDetail
          campaign={campaigns.find((c) => c.id === expandedId)!}
          onClose={() => setExpandedId(null)}
        />
      )}

      {/* Modal de criacao */}
      <CreateCampaignDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  );
}
